import { convertEmployeeNamesToIds, convertEmployeeIdsToNames } from "../../../Utils/employeeUtils";
import { SEED_CONFIGURATION_OPTIONS } from "./constants";

/**
 * Recursively remove 'id' properties from objects and arrays
 * @param {any} obj - The object or value to process
 * @returns {any} The processed object without id properties
 */
export const removeIds = (obj) => {
	if (Array.isArray(obj)) {
		return obj.map((item) => removeIds(item));
	}
	if (obj && typeof obj === "object") {
		const { id, ...objWithoutId } = obj;
		const result = {};
		for (const [key, value] of Object.entries(objWithoutId)) {
			result[key] = removeIds(value);
		}
		return result;
	}
	return obj;
};

/**
 * Remove IDs and convert Date objects to ISO strings
 * @param {any} obj - The object or value to process
 * @returns {any} The processed object without ids and with converted dates
 */
export const removeIdsAndConvertDates = (obj) => {
	if (Array.isArray(obj)) {
		return obj.map(removeIdsAndConvertDates);
	}
	if (obj && typeof obj === "object") {
		const { id, ...objWithoutId } = obj;
		const result = {};
		for (const [key, value] of Object.entries(objWithoutId)) {
			if (value instanceof Date) {
				result[key] = value.toISOString();
			} else {
				result[key] = removeIdsAndConvertDates(value);
			}
		}
		return result;
	}
	return obj;
};

/**
 * Process groups data with proper employee references
 * @param {Array} groups - Array of group objects
 * @param {Array} employees - Array of employee objects
 * @returns {Array} Processed groups with employee names
 */
export const processGroups = (groups, employees) => {
	return (groups || []).map((group) => ({
		...group,
		employees: convertEmployeeIdsToNames(group.employees || [], employees),
		admins: convertEmployeeIdsToNames(group.admins || [], employees),
	}));
};

/**
 * Process goals data with proper employee references and create individual goals for each assignee
 * @param {Array} goals - Array of goal objects
 * @param {Array} employees - Array of employee objects
 * @returns {Array} Processed goals with employee names and individual goals per assignee
 */
export const processGoals = (goals, employees) => {
	return (goals || []).flatMap((goal) => {
		// Destructure to exclude assignees from being spread into the new object
		const { assignees, ...goalWithoutAssignees } = goal;
		const assigneeIds = assignees || [];

		// If no assignees, still create the goal but with no assignee
		if (assigneeIds.length === 0) {
			return [
				{
					...goalWithoutAssignees,
					target: parseInt(goalWithoutAssignees.target) || 0,
					value: parseInt(goalWithoutAssignees.value) || 0,
					assignee: null,
					owner: Array.isArray(goal.owner)
						? convertEmployeeIdsToNames(goal.owner, employees)[0]
						: convertEmployeeIdsToNames([goal.owner], employees)[0],
				},
			];
		}

		// Create a separate goal for each assignee
		return assigneeIds.map((assigneeId) => ({
			...goalWithoutAssignees,
			target: parseInt(goalWithoutAssignees.target) || 0,
			value: parseInt(goalWithoutAssignees.value) || 0,
			assignee: convertEmployeeIdsToNames([assigneeId], employees)[0],
			owner: Array.isArray(goal.owner)
				? convertEmployeeIdsToNames(goal.owner, employees)[0]
				: convertEmployeeIdsToNames([goal.owner], employees)[0],
		}));
	});
};

/**
 * Process review cycles data with proper employee references
 * @param {Array} cycles - Array of review cycle objects
 * @param {Array} employees - Array of employee objects
 * @returns {Array} Processed review cycles with employee names
 */
export const processReviewCycles = (cycles, employees) => {
	return (cycles || []).map((cycle) => ({
		...cycle,
		subjects: convertEmployeeIdsToNames(cycle.subjects || [], employees),
	}));
};

/**
 * Process all entities that have employee references
 * @param {Object} entities - Object containing all entities to process
 * @param {Array} employees - Array of employee objects
 * @returns {Object} Processed entities with proper employee references
 */
export const processEntities = (entities, employees) => {
	return {
		groups: processGroups(entities.groups, employees),
		goals: processGoals(entities.goals, employees),
		reviewCycles: processReviewCycles(entities.reviewCycles, employees),
	};
};

/**
 * Create final configuration data structure
 * @param {Object} config - Configuration metadata
 * @param {Object} dataFields - All data fields
 * @param {Object} randomSeeds - Random seed configuration
 * @returns {Object} Complete configuration object
 */
export const createConfigurationData = (config, dataFields, randomSeeds = {}) => {
	const finalDataFields = {
		random_seeds: randomSeeds,
		...dataFields,
	};

	// Filter out autoflow from company_functions
	const companyFunctions = (config.companyFunctions || []).filter(
		(func) => !func.startsWith("autoflow:") && !func.startsWith("run_etls:")
	); //filter out both autoflow and run etl

	// Check if autoflow was enabled in company_functions
	const autoflowEnabled = (config.companyFunctions || []).some(
		(func) => func.startsWith("autoflow:") && (func.endsWith("enabled") || func.endsWith("true"))
	);

	const runETLsEnabled = (config.companyFunctions || []).some((func) => func.startsWith("run_etls:"));

	return {
		config_name: config.name,
		company_name: config.companyName,
		company_functions: companyFunctions,
		environment: config.environment || "",
		feature_flags: {
			autoflow: autoflowEnabled,
		},
		run_etls: runETLsEnabled,
		...finalDataFields,
	};
};
